import { useRef, useCallback, useEffect } from 'react';

/*!
 * Adapted from jQuery UI core
 *
 * http://jqueryui.com
 *
 * Copyright 2014 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/category/ui-core/
 */
var tabbableNode = /input|select|textarea|button|object/;
var focusSelector = 'a, input, select, textarea, button, object, [tabindex]';

function hidden(el) {
  if (process.env.NODE_ENV === 'test') return false;
  return el.offsetWidth <= 0 && el.offsetHeight <= 0 || el.style.display === 'none';
}

function visible(element) {
  var parentElement = element;

  while (parentElement) {
    if (parentElement === document.body) break;
    if (hidden(parentElement)) return false;
    parentElement = parentElement.parentNode;
  }

  return true;
}

function getElementTabIndex(element) {
  var tabIndex = element.getAttribute('tabindex');
  if (tabIndex === null) tabIndex = undefined;
  return parseInt(tabIndex, 10);
}

function focusable(element) {
  var nodeName = element.nodeName.toLowerCase();
  var isTabIndexNotNaN = !isNaN(getElementTabIndex(element));
  var res = // @ts-ignore
  tabbableNode.test(nodeName) && !element.disabled || (element instanceof HTMLAnchorElement ? element.href || isTabIndexNotNaN : isTabIndexNotNaN);
  return res && visible(element);
}
function tabbable(element) {
  var tabIndex = getElementTabIndex(element);
  var isTabIndexNaN = isNaN(tabIndex);
  return (isTabIndexNaN || tabIndex >= 0) && focusable(element);
}
/**
 * Tabbable elements are elements the user can tab between. This excludes elements with tabIndex=-1
 * */

function findTabbableDescendants(element) {
  return Array.from(element.querySelectorAll(focusSelector)).filter(tabbable);
}

var focusLaterElements = [];
var focusElement = null;
var needToFocus = false;

function handleBlur() {
  needToFocus = true;
}

function handleFocus() {
  if (needToFocus) {
    needToFocus = false;

    if (!focusElement) {
      return;
    }

    if (!focusElement) return;

    if (focusElement.contains(document.activeElement)) {
      return;
    }

    var el = findTabbableDescendants(focusElement)[0] || focusElement;
    el.focus();
  }
}

function markForFocusLater() {
  focusLaterElements.push(document.activeElement);
}
function returnFocus() {
  var toFocus = null;

  try {
    toFocus = focusLaterElements.pop();
    if (toFocus) toFocus.focus();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(['You tried to return focus to', toFocus, 'but it is not in the DOM anymore'].join(' '));
  }
}
function setupScopedFocus(element) {
  focusElement = element;
  document.addEventListener('focusout', handleBlur, false);
  document.addEventListener('focusin', handleFocus, true);
}
function teardownScopedFocus() {
  focusElement = null;
  document.removeEventListener('focusout', handleBlur);
  document.removeEventListener('focusin', handleFocus);
}

/**
 * Controls tabbing, so it stays inside the node element
 * @param node
 * @param event
 */

function scopeTab(node, event) {
  var tabbable = findTabbableDescendants(node);

  if (!tabbable.length) {
    event.preventDefault();
    return;
  }

  var finalTabbable = tabbable[event.shiftKey ? 0 : tabbable.length - 1];
  var leavingFinalTabbable = finalTabbable === document.activeElement || // handle immediate shift+tab after opening with mouse
  node === document.activeElement;
  if (!leavingFinalTabbable) return;
  event.preventDefault();
  var target = tabbable[event.shiftKey ? tabbable.length - 1 : 0];
  if (target) target.focus();
}

/**
 * Sets `aria-hidden` on all elements on the body, except for the active node.
 **/
function createAriaHider(containerNode, selector) {
  if (selector === void 0) {
    selector = 'body > :not(script)';
  }

  var rootNodes = Array.from(document.querySelectorAll(selector)).map(function (node) {
    if (node.contains(containerNode)) {
      return undefined;
    }

    var ariaHidden = node.getAttribute('aria-hidden');

    if (ariaHidden === null || ariaHidden === 'false') {
      node.setAttribute('aria-hidden', 'true');
    }

    return {
      node: node,
      ariaHidden: ariaHidden
    };
  });
  return function () {
    rootNodes.forEach(function (item) {
      if (!item) return;

      if (item.ariaHidden === null) {
        item.node.removeAttribute('aria-hidden');
      } else {
        item.node.setAttribute('aria-hidden', item.ariaHidden);
      }
    });
  };
}

/**
 *  Traps focus to DOM node. Use this with a modal to ensure the user focus doesn't leave.
 * */
function useFocusTrap(active, options) {
  if (active === void 0) {
    active = true;
  }

  if (options === void 0) {
    options = {};
  }

  var ref = useRef();
  var restoreAria = useRef(null);
  var setRef = useCallback(function (node) {
    if (restoreAria.current) {
      restoreAria.current();
    }

    if (ref.current) {
      returnFocus();
      teardownScopedFocus();
    }

    if (active && node) {
      setupScopedFocus(node);
      markForFocusLater();

      var processNode = function processNode(node) {
        // See if we should disable aria
        restoreAria.current = !options.disableAriaHider ? createAriaHider(node) : null; // Find the initial focus element

        var focusElement = null;

        if (options.focusSelector) {
          focusElement = typeof options.focusSelector === 'string' ? node.querySelector(options.focusSelector) : options.focusSelector;
        }

        if (!focusElement) {
          var children = Array.from(node.querySelectorAll(focusSelector));
          focusElement = // Prefer tabbable elements, But fallback to any focusable element
          children.find(tabbable) || // But fallback to any focusable element
          children.find(focusable) || // Nothing found
          null; // If everything else fails, see if the node itself can handle focus

          if (!focusElement && focusable(node)) focusElement = node;
        }

        if (focusElement) {
          // Set the initial focus inside the traps
          focusElement.focus();
        } else {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('[useFocusTrap]: Failed to find a focusable element after activating the focus trap. Make sure to include at an element that can recieve focus. As a fallback, you can also set "tabIndex={-1}" on the focus trap node.', node);
          }
        }
      }; // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.


      setTimeout(function () {
        if (node.ownerDocument) {
          processNode(node);
        } else if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[useFocusTrap]: The focus trap is not part of the DOM yet, so it is unable to correctly set focus. Make sure to render the ref node.', node);
        }
      });
      ref.current = node;
    } else {
      ref.current = null;
    }
  }, [active, options.focusSelector, options.disableAriaHider]);
  useEffect(function () {
    if (!active) return undefined;

    var handleKeyDown = function handleKeyDown(event) {
      if (event.key === 'Tab' && ref.current) {
        scopeTab(ref.current, event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active]);
  return setRef;
}

export default useFocusTrap;
